import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import {
  Plus,
  Search,
  Users,
  Edit,
  Trash2,
  MoreVertical,
  Mail,
  Calendar,
  Tag
} from 'lucide-react';
import toast from 'react-hot-toast';

const ListsPage = () => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'static'
  });

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getMarketingLists();
      setLists(response.data.data || []);
    } catch (err) {
      toast.error('Failed to load lists');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (list = null) => {
    if (list) {
      setEditingList(list);
      setFormData({
        name: list.name,
        description: list.description || '',
        type: list.type || 'static'
      });
    } else {
      setEditingList(null);
      setFormData({
        name: '',
        description: '',
        type: 'static'
      });
    }
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingList(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingList) {
        await adminApi.updateMarketingList(editingList.id, formData);
        toast.success('List updated successfully');
      } else {
        await adminApi.createMarketingList(formData);
        toast.success('List created successfully');
      }
      handleCloseModal();
      fetchLists();
    } catch (err) {
      toast.error(editingList ? 'Failed to update list' : 'Failed to create list');
      console.error(err);
    }
  };

  const handleDelete = async (list) => {
    if (!confirm(`Are you sure you want to delete "${list.name}"?`)) return;

    try {
      await adminApi.deleteMarketingList(list.id);
      toast.success('List deleted successfully');
      fetchLists();
    } catch (err) {
      toast.error('Failed to delete list');
      console.error(err);
    }
  };

  const filteredLists = lists.filter((list) =>
    list.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketing Lists</h1>
          <p className="text-gray-500 mt-1">Organize contacts into targeted lists</p>
        </div>
        <button onClick={() => handleOpenModal()} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          New List
        </button>
      </div>

      {/* Search */}
      <div className="card p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search lists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      {/* Lists Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredLists.map((list) => (
          <div key={list.id} className="card p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="p-2 bg-primary-100 rounded-lg mr-3">
                  <Users className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{list.name}</h3>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      list.type === 'dynamic'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {list.type}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleOpenModal(list)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(list)}
                  className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {list.description && (
              <p className="text-sm text-gray-500 mb-4">{list.description}</p>
            )}

            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center text-gray-500">
                  <Users className="w-4 h-4 mr-2" />
                  Contacts
                </div>
                <span className="font-medium text-gray-900">{list.contactCount || 0}</span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center text-gray-500">
                  <Mail className="w-4 h-4 mr-2" />
                  Campaigns
                </div>
                <span className="font-medium text-gray-900">{list.campaignCount || 0}</span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center text-gray-500">
                  <Calendar className="w-4 h-4 mr-2" />
                  Created
                </div>
                <span className="text-gray-600">
                  {new Date(list.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="border-t border-gray-100 mt-4 pt-4">
              <div className="flex gap-2">
                <button className="flex-1 btn-secondary text-sm">
                  <Users className="w-4 h-4 mr-1" />
                  View Contacts
                </button>
                <button className="flex-1 btn-secondary text-sm">
                  <Mail className="w-4 h-4 mr-1" />
                  Send Campaign
                </button>
              </div>
            </div>
          </div>
        ))}

        {filteredLists.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            {searchQuery ? 'No lists match your search' : 'No lists created yet'}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingList ? 'Edit List' : 'Create New List'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  List Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input"
                  placeholder="e.g., Newsletter Subscribers"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input"
                  rows={3}
                  placeholder="Describe this list..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  List Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="input"
                >
                  <option value="static">Static (manually add contacts)</option>
                  <option value="dynamic">Dynamic (based on rules)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.type === 'static'
                    ? 'You will manually add and remove contacts from this list.'
                    : 'Contacts will be automatically added based on matching criteria.'}
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                <button type="button" onClick={handleCloseModal} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingList ? 'Update List' : 'Create List'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListsPage;
